/**
 * Rules for turning the browser host into a Billing organization code.
 *
 * Kept out of `tenant-context.service.ts` so the mapping can be tweaked without touching the
 * service logic. The org code we send to Billing must match a tenant `Code` registered there.
 *
 * Resolution order (see `TenantContextService.resolveOrgCode`):
 *   1. `orgAliases[label]` — explicit override for a subdomain label (highest priority).
 *   2. strip any matching `stripPrefixes` entry off the label (`dev-codewise` → `codewise`).
 *   3. `ignoredHosts` labels (and local/apex hosts) → no tenant; fall back to `?org=` / devOrgCode.
 *   4. otherwise the (stripped) label IS the org code.
 */
export const TENANT_RESOLUTION = {
  /**
   * Environment prefixes carried on deploy subdomains, stripped before using the label as the
   * org code. Works across environments: prod `codewise.bizcombotech.com` has no prefix to strip,
   * dev `dev-codewise.bizcombotech.com` → `codewise`. Add `staging`/`uat` here if those hosts ship.
   */
  stripPrefixes: ['dev', 'staging', 'uat', 'test'] as string[],

  /** Subdomains that are not customer tenants and should never resolve to an org. */
  ignoredHosts: ['www', 'app'] as string[],

  /**
   * Explicit subdomain-label → org-code overrides. Wins over prefix stripping. Use only for hosts
   * that don't follow the `<prefix>-<org>` convention. Keyed by the lowercased first label.
   * e.g. { 'dev-codewise': 'codewise' }
   */
  orgAliases: {} as Record<string, string>,
};
